import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ForAgents.css';

const ForAgents: React.FC = () => {
  const [callsPerMonth, setCallsPerMonth] = useState<number>(20);
  const [conversionRate, setConversionRate] = useState<number>(15);
  const [avgCommission, setAvgCommission] = useState<number>(400);

  const monthlyCost = callsPerMonth * 5; // Using $5 per call (standard pricing)
  const expectedBookings = (callsPerMonth * conversionRate) / 100;
  const expectedRevenue = expectedBookings * avgCommission;
  const netProfit = expectedRevenue - monthlyCost;
  const roi = monthlyCost > 0 ? ((netProfit / monthlyCost) * 100).toFixed(0) : 0;

  return (
    <div className="for-agents">
      <section className="page-hero">
        <div className="container">
          <h1>Built for Independent Travel Advisors</h1>
          <p className="subtitle">The smartest way to grow your cruise business</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>How It Works for You</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-icon">📝</div>
              <h3>1. Apply & Get Approved</h3>
              <p>
                Quick application and manual review process. We verify your travel advisor
                credentials and ensure you're a good fit for our network.
              </p>
            </div>
            <div className="process-step">
              <div className="step-icon">📱</div>
              <h3>2. Download the App</h3>
              <p>
                We use Aircall for call routing. Download the mobile app, and you're ready
                to receive calls on your smartphone.
              </p>
            </div>
            <div className="process-step">
              <div className="step-icon">🎯</div>
              <h3>3. Set Your Availability</h3>
              <p>
                Toggle Available/Unavailable anytime. Only receive calls when you're ready.
                Complete control over your schedule and spend.
              </p>
            </div>
            <div className="process-step">
              <div className="step-icon">📞</div>
              <h3>4. Receive High-Intent Calls</h3>
              <p>
                When a cruise shopper calls us, we route them to available agents in real-time.
                These are people actively looking to book.
              </p>
            </div>
            <div className="process-step">
              <div className="step-icon">💼</div>
              <h3>5. Book & Earn Commissions</h3>
              <p>
                Help customers find their perfect cruise, book the trip, and earn your full
                commission. You keep 100% of every commission.
              </p>
            </div>
            <div className="process-step">
              <div className="step-icon">💳</div>
              <h3>6. Get Billed Monthly</h3>
              <p>
                Automatic billing on the 1st of each month via Stripe. Only for qualified
                calls you actually answered.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section gray">
        <div className="container">
          <h2>What Makes a Call Billable?</h2>
          <div className="billable-grid">
            <div className="billable-card yes">
              <h3>✓ Billable Calls</h3>
              <p>You're only charged when ALL of these criteria are met:</p>
              <ul>
                <li>Duration: 90+ seconds</li>
                <li>Intent: Cruise-related inquiry</li>
                <li>Legitimate: Real customer (not spam)</li>
                <li>First Contact: First call from this number</li>
                <li>Connected: You answered and had a conversation</li>
              </ul>
            </div>
            <div className="billable-card no">
              <h3>✗ Not Billable</h3>
              <p>You're NOT charged for:</p>
              <ul>
                <li>Calls under 90 seconds</li>
                <li>Non-cruise inquiries</li>
                <li>Spam or robocalls</li>
                <li>Duplicate calls within 24 hours</li>
                <li>Technical issues preventing conversation</li>
                <li>Follow-up calls from your assigned customers (FREE forever!)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>ROI Calculator</h2>
          <p className="calculator-intro">
            See your potential return on investment. Adjust the sliders based on your goals:
          </p>
          <div className="roi-calculator">
            <div className="calculator-inputs">
              <div className="input-group">
                <label>Calls Per Month: <strong>{callsPerMonth}</strong></label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={callsPerMonth}
                  onChange={(e) => setCallsPerMonth(Number(e.target.value))}
                />
              </div>
              <div className="input-group">
                <label>Conversion Rate: <strong>{conversionRate}%</strong></label>
                <input
                  type="range"
                  min="5"
                  max="30"
                  value={conversionRate}
                  onChange={(e) => setConversionRate(Number(e.target.value))}
                />
              </div>
              <div className="input-group">
                <label>Average Commission: <strong>${avgCommission}</strong></label>
                <input
                  type="range"
                  min="200"
                  max="800"
                  step="50"
                  value={avgCommission}
                  onChange={(e) => setAvgCommission(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="calculator-results">
              <div className="result-card">
                <div className="result-label">Monthly Cost</div>
                <div className="result-value">${monthlyCost.toFixed(0)}</div>
              </div>
              <div className="result-card">
                <div className="result-label">Expected Bookings</div>
                <div className="result-value">{expectedBookings.toFixed(1)}</div>
              </div>
              <div className="result-card">
                <div className="result-label">Expected Revenue</div>
                <div className="result-value">${expectedRevenue.toFixed(0)}</div>
              </div>
              <div className="result-card highlight">
                <div className="result-label">Net Profit</div>
                <div className="result-value">${netProfit.toFixed(0)}</div>
              </div>
              <div className="result-card highlight">
                <div className="result-label">ROI</div>
                <div className="result-value">{roi}%</div>
              </div>
            </div>
          </div>
          <p className="calculator-note">
            *Calculator uses $5/call (standard pricing). Results are estimates based on your inputs.
            Actual results may vary based on your sales skills, follow-up process, and market conditions.
          </p>
        </div>
      </section>

      <section className="section gray">
        <div className="container">
          <h2>Success Stories</h2>
          <div className="success-grid">
            <div className="success-card">
              <div className="success-quote">
                "I was spending $1,200/month on Facebook ads with terrible results. Switched to
                Cruise Spotter Connect and now spend $400-600/month with 3x the bookings."
              </div>
              <div className="success-author">— Sarah M., Tampa, FL</div>
              <div className="success-stats">
                <div className="stat">
                  <div className="stat-value">25%</div>
                  <div className="stat-label">Conversion Rate</div>
                </div>
                <div className="stat">
                  <div className="stat-value">$3,800</div>
                  <div className="stat-label">Avg Monthly Profit</div>
                </div>
              </div>
            </div>
            <div className="success-card">
              <div className="success-quote">
                "The quality is unmatched. These people are ready to book NOW. I close 1 in 4
                calls, which is insane compared to other lead sources."
              </div>
              <div className="success-author">— Michael R., Denver, CO</div>
              <div className="success-stats">
                <div className="stat">
                  <div className="stat-value">28%</div>
                  <div className="stat-label">Conversion Rate</div>
                </div>
                <div className="stat">
                  <div className="stat-value">$5,200</div>
                  <div className="stat-label">Avg Monthly Profit</div>
                </div>
              </div>
            </div>
            <div className="success-card">
              <div className="success-quote">
                "I love the flexibility. When I'm busy, I toggle off. When I need leads, I
                toggle on. It's that simple."
              </div>
              <div className="success-author">— Jennifer L., Phoenix, AZ</div>
              <div className="success-stats">
                <div className="stat">
                  <div className="stat-value">18%</div>
                  <div className="stat-label">Conversion Rate</div>
                </div>
                <div className="stat">
                  <div className="stat-value">$2,400</div>
                  <div className="stat-label">Avg Monthly Profit</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container cta-section">
          <h2>Ready to Join Our Network?</h2>
          <p>
            No contracts, no monthly fees, no risk. Start receiving high-quality cruise leads today.
          </p>
          <Link to="/contact" className="btn btn-large btn-primary">Apply Now</Link>
        </div>
      </section>
    </div>
  );
};

export default ForAgents;
